/**
 * Vista "Costo de venta" (RES-47): KPIs, costo de venta vs. ventas cobradas por período (un solo
 * eje en $, barras + línea) y tablas por período y por local.
 *
 * `grossCogs` es la suma del costo de los movimientos SALE del kardex; `cogs` descuenta lo que
 * volvió al stock por reversas.
 */
import React, { useMemo } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatCurrency } from '../../utils/formatCurrency';
import {
  cogsRestaurantRows,
  formatCompactCurrency,
  formatPct,
  formatPeriod,
} from '../../utils/inventoryReports';
import { ChartLegend, ChartTooltip, KpiTiles, SortableTable, axisProps } from './ReportParts';

const num = (v) => Number(v) || 0;

function CogsReport({ data, t, locale, colors, nameOf }) {
  const groupBy = data?.groupBy || 'day';
  const totals = data?.totals || {};
  const periods = useMemo(
    () =>
      (data?.periods || []).map((p) => ({ ...p, label: formatPeriod(p.period, groupBy, locale) })),
    [data, groupBy, locale]
  );
  const restaurantRows = useMemo(
    () => (data?.consolidated ? cogsRestaurantRows(data.byRestaurant, nameOf) : []),
    [data, nameOf]
  );

  const marginClass = (row) => (num(row.grossMargin) < 0 ? 'is-negative' : '');
  const moneyColumns = [
    {
      key: 'grossCogs',
      label: t('inventory.reports.columns.grossCogs'),
      align: 'right',
      sortValue: (r) => num(r.grossCogs),
      render: (r) => formatCurrency(r.grossCogs),
    },
    {
      key: 'reversals',
      label: t('inventory.reports.columns.reversals'),
      align: 'right',
      sortValue: (r) => num(r.reversals),
      render: (r) => formatCurrency(r.reversals),
    },
    {
      key: 'cogs',
      label: t('inventory.reports.columns.cogs'),
      align: 'right',
      sortValue: (r) => num(r.cogs),
      render: (r) => formatCurrency(r.cogs),
    },
    {
      key: 'revenue',
      label: t('inventory.reports.columns.revenue'),
      align: 'right',
      sortValue: (r) => num(r.revenue),
      render: (r) => formatCurrency(r.revenue),
    },
    {
      key: 'orders',
      label: t('inventory.reports.columns.orders'),
      align: 'right',
      sortValue: (r) => num(r.orders),
      render: (r) => num(r.orders),
    },
    {
      key: 'grossMargin',
      label: t('inventory.reports.columns.grossMargin'),
      align: 'right',
      sortValue: (r) => num(r.grossMargin),
      cellClass: marginClass,
      render: (r) => formatCurrency(r.grossMargin),
    },
    {
      key: 'grossMarginPct',
      label: t('inventory.reports.columns.grossMarginPct'),
      align: 'right',
      sortValue: (r) => r.grossMarginPct,
      cellClass: marginClass,
      render: (r) => formatPct(r.grossMarginPct, locale),
    },
    {
      key: 'uncosted',
      label: t('inventory.reports.columns.uncosted'),
      align: 'right',
      sortValue: (r) => num(r.uncosted),
      render: (r) => num(r.uncosted) || '—',
    },
  ];

  const tooltipRows = (payload) => {
    const p = payload[0].payload;
    return [
      {
        key: 'cogs',
        label: t('inventory.reports.cogs.seriesCogs'),
        value: formatCurrency(p.cogs),
        color: colors.series1,
      },
      {
        key: 'revenue',
        label: t('inventory.reports.cogs.seriesRevenue'),
        value: formatCurrency(p.revenue),
        color: colors.series2,
      },
      {
        key: 'margin',
        label: t('inventory.reports.columns.grossMargin'),
        value: `${formatCurrency(p.grossMargin)} (${formatPct(p.grossMarginPct, locale)})`,
      },
    ];
  };

  return (
    <div className="report-view" data-view="cogs">
      <KpiTiles
        tiles={[
          {
            key: 'cogs',
            label: t('inventory.reports.cogs.kpiCogs'),
            value: formatCurrency(totals.cogs),
            sub: t('inventory.reports.cogs.kpiCogsSub', {
              gross: formatCurrency(totals.grossCogs),
              reversals: formatCurrency(totals.reversals),
            }),
          },
          {
            key: 'revenue',
            label: t('inventory.reports.cogs.kpiRevenue'),
            value: formatCurrency(totals.revenue),
            sub: t('inventory.reports.cogs.kpiOrders', { count: num(totals.orders) }),
          },
          {
            key: 'margin',
            label: t('inventory.reports.cogs.kpiMargin'),
            value: formatCurrency(totals.grossMargin),
            sub: formatPct(totals.grossMarginPct, locale),
            tone: num(totals.grossMargin) < 0 ? 'negative' : undefined,
          },
          {
            key: 'cogsPct',
            label: t('inventory.reports.cogs.kpiCogsPct'),
            value: formatPct(totals.cogsPct, locale),
            sub:
              num(totals.uncosted) > 0
                ? t('inventory.reports.notes.uncosted', { count: num(totals.uncosted) })
                : undefined,
          },
        ]}
      />
      <p className="report-hint">{t('inventory.reports.cogs.grossHint')}</p>

      <section className="report-card">
        <div className="report-card-header">
          <h3>{t('inventory.reports.cogs.chartTitle')}</h3>
          <ChartLegend
            items={[
              { key: 'cogs', label: t('inventory.reports.cogs.seriesCogs'), color: colors.series1 },
              {
                key: 'revenue',
                label: t('inventory.reports.cogs.seriesRevenue'),
                color: colors.series2,
                shape: 'line',
              },
            ]}
          />
        </div>
        {periods.length > 0 ? (
          <div
            className="report-chart"
            role="img"
            aria-label={t('inventory.reports.cogs.chartTitle')}
          >
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={periods} margin={{ top: 8, right: 16, left: 8, bottom: 4 }}>
                <CartesianGrid vertical={false} stroke={colors.grid} />
                <XAxis dataKey="label" {...axisProps(colors)} minTickGap={12} />
                <YAxis
                  {...axisProps(colors)}
                  width={72}
                  tickFormatter={(v) => formatCompactCurrency(v, locale)}
                />
                <Tooltip
                  cursor={{ fill: colors.grid, opacity: 0.4 }}
                  content={<ChartTooltip rows={tooltipRows} />}
                />
                <Bar
                  dataKey="cogs"
                  fill={colors.series1}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={28}
                  isAnimationActive={false}
                />
                <Line
                  dataKey="revenue"
                  type="monotone"
                  stroke={colors.series2}
                  strokeWidth={2}
                  dot={
                    periods.length <= 31
                      ? { r: 4, fill: colors.series2, stroke: colors.surface, strokeWidth: 2 }
                      : false
                  }
                  activeDot={{ r: 5, fill: colors.series2, stroke: colors.surface, strokeWidth: 2 }}
                  isAnimationActive={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="report-hint">{t('inventory.reports.states.noChartData')}</p>
        )}
      </section>

      {restaurantRows.length > 0 && (
        <section className="report-card">
          <h3>{t('inventory.reports.compareTitle')}</h3>
          <SortableTable
            t={t}
            testId="cogs-by-restaurant"
            rows={restaurantRows}
            rowKey={(r) => r.restaurantId}
            defaultSort={{ key: 'cogs', direction: 'desc' }}
            columns={[
              {
                key: 'name',
                label: t('inventory.reports.columns.restaurant'),
                sortValue: (r) => r.name,
                render: (r) => r.name,
              },
              ...moneyColumns,
            ]}
          />
        </section>
      )}

      <section className="report-card">
        <h3>{t('inventory.reports.cogs.tableTitle')}</h3>
        <SortableTable
          t={t}
          testId="cogs-table"
          rows={periods}
          rowKey={(r) => r.period}
          defaultSort={{ key: 'period', direction: 'asc' }}
          columns={[
            {
              key: 'period',
              label: t(`inventory.reports.groupBy.${groupBy}`),
              sortValue: (r) => r.period,
              render: (r) => r.label,
            },
            ...moneyColumns,
          ]}
        />
      </section>
    </div>
  );
}

export default CogsReport;
